import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Search, MapPin } from "lucide-react";
import { CALCULATOR_LIST, getCalculator } from "@/lib/calculators";
import { US_STATES } from "@/lib/states";

/**
 * State-picker dialog: pick a calculator + state, get routed directly to the
 * state-adjusted landing page (e.g. /bathroom-remodel-cost-california). Skips
 * the 4-step wizard for users who already know their state.
 */
export default function StatePickerDialog({ open, onOpenChange }) {
  const navigate = useNavigate();
  const [calcSlug, setCalcSlug] = useState("bathroom");
  const [query, setQuery] = useState("");

  // Reset search when dialog re-opens so the next open starts fresh.
  useEffect(() => {
    if (!open) setQuery("");
  }, [open]);

  const filteredStates = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return US_STATES;
    return US_STATES.filter(
      (s) =>
        s.name.toLowerCase().includes(needle) ||
        s.code.toLowerCase() === needle ||
        s.slug.includes(needle.replace(/\s+/g, "-")),
    );
  }, [query]);

  const handlePick = (state) => {
    const calc = getCalculator(calcSlug);
    if (!calc) return;
    onOpenChange(false);
    navigate(`${calc.statePathBase}-${state.slug}`);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-3xl gap-0 p-0 sm:rounded-2xl"
        data-testid="state-picker-dialog"
      >
        <DialogHeader className="border-b border-slate-100 px-6 py-4">
          <DialogTitle className="font-display text-lg font-semibold text-slate-900">
            Jump straight to state pricing
          </DialogTitle>
          <DialogDescription className="text-sm text-slate-500">
            Pick a project type + your state — we'll send you straight to the state-adjusted
            calculator without the multi-step setup.
          </DialogDescription>
        </DialogHeader>

        {/* Calculator picker pills */}
        <div className="border-b border-slate-100 px-6 py-4">
          <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-slate-500">
            Project type
          </p>
          <div className="flex flex-wrap gap-1.5" data-testid="state-picker-calcs">
            {CALCULATOR_LIST.map((c) => (
              <button
                key={c.slug}
                type="button"
                onClick={() => setCalcSlug(c.slug)}
                className={`rounded-full px-3 py-1.5 text-xs font-medium transition ${
                  calcSlug === c.slug
                    ? "bg-slate-900 text-white"
                    : "border border-slate-200 text-slate-700 hover:border-slate-300"
                }`}
                data-testid={`state-picker-calc-${c.slug}`}
              >
                {c.shortName || c.name}
              </button>
            ))}
          </div>
        </div>

        {/* Search box */}
        <div className="border-b border-slate-100 px-6 py-3">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              autoFocus
              placeholder="Search 50 states…"
              className="h-10 w-full rounded-full border border-slate-200 bg-white pl-10 pr-4 text-sm text-slate-900 placeholder:text-slate-400 focus:border-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-200"
              data-testid="state-picker-search"
            />
          </div>
        </div>

        {/* States grid */}
        <div className="max-h-[420px] overflow-y-auto px-6 py-4">
          {filteredStates.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-500">No states match "{query}".</p>
          ) : (
            <ul className="grid grid-cols-2 gap-1 sm:grid-cols-3 lg:grid-cols-4" data-testid="state-picker-grid">
              {filteredStates.map((s) => (
                <li key={s.code}>
                  <button
                    type="button"
                    onClick={() => handlePick(s)}
                    className="group flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm transition hover:bg-slate-50"
                    data-testid={`state-picker-state-${s.code}`}
                  >
                    <span className="inline-flex h-7 w-7 shrink-0 items-center justify-center rounded bg-slate-100 text-xs font-semibold text-slate-700 group-hover:bg-slate-900 group-hover:text-white">
                      {s.code}
                    </span>
                    <span className="text-slate-700 group-hover:text-slate-900">{s.name}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="flex items-center justify-between gap-2 border-t border-slate-100 bg-slate-50/50 px-6 py-3 text-xs text-slate-500">
          <span className="inline-flex items-center gap-1">
            <MapPin className="h-3 w-3" />
            50 states · state-adjusted pricing for every project
          </span>
          <kbd className="rounded border border-slate-300 bg-white px-1.5 py-0.5 font-mono text-[10px] text-slate-500">
            esc
          </kbd>
        </footer>
      </DialogContent>
    </Dialog>
  );
}
